import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { AuthContext } from "@/App";
import { toast } from "sonner";
import { 
  Handshake, 
  FileText, 
  Search, 
  Scale, 
  Wallet, 
  CheckCircle2, 
  Clock,
  Building2,
  ArrowLeft,
  ArrowRight,
  User,
  Calendar,
  Mail,
  Phone,
  MessageCircle,
  Upload,
  XCircle,
  AlertTriangle,
  Download,
  ExternalLink,
  Trash2,
  Loader2,
  File,
  FileImage,
  FileSpreadsheet
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EscrowPayment from "@/components/EscrowPayment";

const DEAL_STAGES = [
  { key: "interest", label: "Interest Shown", icon: Handshake, color: "bg-blue-500", description: "Buyer has shown interest in the listing" },
  { key: "nda_signed", label: "NDA Signed", icon: FileText, color: "bg-indigo-500", description: "Both parties have signed the NDA" },
  { key: "due_diligence", label: "Due Diligence", icon: Search, color: "bg-purple-500", description: "Buyer is reviewing business documents" },
  { key: "loi_signed", label: "LOI Signed", icon: Scale, color: "bg-pink-500", description: "Letter of Intent has been signed" },
  { key: "escrow_funded", label: "Escrow Funded", icon: Wallet, color: "bg-orange-500", description: "Commission escrow has been funded" },
  { key: "closing", label: "Closing", icon: Clock, color: "bg-amber-500", description: "Final paperwork and transfer in progress" },
  { key: "completed", label: "Completed", icon: CheckCircle2, color: "bg-emerald-500", description: "Deal successfully completed!" }
];

const formatCurrency = (amount) => {
  if (!amount) return "TBD";
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const getStageIndex = (stage) => DEAL_STAGES.findIndex(s => s.key === stage);

export default function DealDetail() {
  const { dealId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [advanceStageDialog, setAdvanceStageDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [stageNotes, setStageNotes] = useState("");
  const [processing, setProcessing] = useState(false);
  
  // Document upload state
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [docType, setDocType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);

  const documentTypes = [
    { value: "nda", label: "NDA (Non-Disclosure Agreement)" },
    { value: "loi", label: "LOI (Letter of Intent)" },
    { value: "financial", label: "Financial Statements" },
    { value: "tax_returns", label: "Tax Returns" },
    { value: "legal", label: "Legal Documents" },
    { value: "due_diligence", label: "Due Diligence Report" },
    { value: "sale_agreement", label: "Sale Agreement" },
    { value: "other", label: "Other Document" }
  ];

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    fetchDeal();
  }, [dealId, user, navigate]);

  const fetchDeal = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/deals/${dealId}`);
      setDeal(data);
    } catch (error) {
      toast.error("Failed to fetch deal details");
      navigate("/deals");
    } finally {
      setLoading(false);
    }
  };

  const handleAdvanceStage = async () => {
    if (!deal) return;
    
    const currentIndex = getStageIndex(deal.current_stage);
    if (currentIndex >= DEAL_STAGES.length - 1) return;
    
    const nextStage = DEAL_STAGES[currentIndex + 1].key;
    
    setProcessing(true);
    try {
      await api.patch(`/deals/${dealId}/stage`, {
        stage: nextStage,
        notes: stageNotes
      });
      toast.success(`Deal advanced to ${DEAL_STAGES[currentIndex + 1].label}`);
      setAdvanceStageDialog(false);
      setStageNotes("");
      fetchDeal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to advance deal stage");
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelDeal = async () => {
    setProcessing(true);
    try {
      await api.patch(`/deals/${dealId}/cancel?reason=${encodeURIComponent(cancelReason)}`);
      toast.success("Deal has been cancelled");
      setCancelDialog(false);
      fetchDeal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel deal");
    } finally {
      setProcessing(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File too large. Maximum size is 10MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile || !docType) {
      toast.error("Please select a file and document type");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("doc_type", docType);

      await api.post(`/deals/${dealId}/documents`, formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      
      toast.success("Document uploaded successfully");
      setUploadDialog(false);
      setSelectedFile(null);
      setDocType("");
      fetchDeal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    setDeletingDocId(docId);
    try {
      await api.delete(`/deals/${dealId}/documents/${docId}`);
      toast.success("Document deleted");
      fetchDeal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete document");
    } finally {
      setDeletingDocId(null);
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return FileImage;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
    if (['pdf'].includes(ext)) return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  if (loading || !deal) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-12 text-center">
          Loading deal details...
        </div>
      </div>
    );
  }

  const currentStageIndex = getStageIndex(deal.current_stage);
  const currentStageInfo = DEAL_STAGES[currentStageIndex];
  const nextStageInfo = currentStageIndex < DEAL_STAGES.length - 1 ? DEAL_STAGES[currentStageIndex + 1] : null;
  const userRole = deal.buyer_id === user?.id ? "buyer" : "seller";
  const otherParty = userRole === "buyer" 
    ? { name: deal.seller_name, email: deal.seller_email, role: "Seller" }
    : { name: deal.buyer_name, email: deal.buyer_email, role: "Buyer" };
  const isActive = deal.status === "active";
  const isCompleted = deal.status === "completed";
  const isCancelled = deal.status === "cancelled";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link to="/deals" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Deals
        </Link>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-slate-900">{deal.listing_name}</h1>
              <Badge className={`${
                isCompleted ? 'bg-emerald-600' : 
                isCancelled ? 'bg-red-600' : 
                'bg-blue-600'
              }`}>
                {deal.status.charAt(0).toUpperCase() + deal.status.slice(1)}
              </Badge>
            </div>
            <p className="text-slate-600">
              Deal ID: <span className="font-mono text-sm">{deal.id.slice(0, 8)}...</span>
            </p>
          </div>
          
          <div className="flex gap-3">
            {isActive && (
              <>
                <Link to={`/listing/${deal.listing_id}`}>
                  <Button variant="outline">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View Listing
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setCancelDialog(true)}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancel Deal
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Deal Value & Commission Card */}
        <Card className="mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-slate-500 mb-1">Deal Value</p>
                <p className="text-3xl font-bold text-slate-900">{formatCurrency(deal.deal_value)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Commission Rate</p>
                <p className="text-2xl font-bold text-slate-900">
                  {deal.commission_rate ? `${(deal.commission_rate * 100).toFixed(0)}%` : "TBD"}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Commission Amount</p>
                <p className="text-2xl font-bold text-purple-600">{formatCurrency(deal.commission_amount)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Escrow Status</p>
                <Badge variant={deal.escrow_status === "funded" ? "default" : "secondary"} className="text-sm">
                  {deal.escrow_status.replace("_", " ").toUpperCase()}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stage Progress */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Deal Progress</CardTitle>
            <CardDescription>Track the stages of your transaction</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              {/* Progress Line */}
              <div className="absolute top-6 left-0 right-0 h-1 bg-slate-200 rounded">
                <div 
                  className="h-full bg-emerald-500 rounded transition-all duration-500"
                  style={{ width: `${(currentStageIndex / (DEAL_STAGES.length - 1)) * 100}%` }}
                />
              </div>
              
              {/* Stages */}
              <div className="relative flex justify-between">
                {DEAL_STAGES.map((stage, index) => {
                  const isCompleted = index < currentStageIndex;
                  const isCurrent = index === currentStageIndex;
                  const Icon = stage.icon;
                  
                  return (
                    <div key={stage.key} className="flex flex-col items-center" style={{ width: '14%' }}>
                      <div className={`
                        relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-4 border-white shadow-md
                        ${isCompleted ? 'bg-emerald-500 text-white' : isCurrent ? stage.color + ' text-white' : 'bg-slate-100 text-slate-400'}
                      `}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <p className={`mt-3 text-xs font-medium text-center ${isCurrent ? 'text-slate-900' : 'text-slate-500'}`}>
                        {stage.label}
                      </p>
                      {isCurrent && (
                        <p className="mt-1 text-xs text-slate-500 text-center max-w-[100px]">
                          {stage.description}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Advance Stage Button */}
            {isActive && nextStageInfo && (
              <div className="mt-8 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">Ready to move to the next stage?</p>
                    <p className="text-sm text-slate-500">
                      Next: {nextStageInfo.label} - {nextStageInfo.description}
                    </p>
                  </div>
                  <Button 
                    onClick={() => setAdvanceStageDialog(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Advance to {nextStageInfo.label}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Other Party Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {otherParty.role} Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-slate-500">Name</p>
                <p className="font-medium text-slate-900">{otherParty.name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="font-medium text-slate-900 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  {otherParty.email}
                </p>
              </div>
              <div className="pt-4 border-t">
                <Link to="/messages">
                  <Button variant="outline" className="w-full">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Send Message
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Documents */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Documents
                  </CardTitle>
                  <CardDescription>
                    {deal.documents?.length || 0} documents uploaded
                  </CardDescription>
                </div>
                {isActive && (
                  <Button 
                    size="sm" 
                    onClick={() => setUploadDialog(true)}
                    data-testid="upload-document-button"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {deal.documents?.length > 0 ? (
                <div className="space-y-3">
                  {deal.documents.map((doc, index) => {
                    const FileIcon = getFileIcon(doc.name);
                    return (
                      <div key={doc.id || index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <FileIcon className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{doc.name}</p>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <Badge variant="outline" className="text-xs capitalize">
                                {doc.type.replace(/_/g, " ")}
                              </Badge>
                              {doc.size && <span>{formatFileSize(doc.size)}</span>}
                              {doc.uploaded_by_name && <span>by {doc.uploaded_by_name}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <a href={doc.url} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="ghost" title="Download">
                              <Download className="w-4 h-4" />
                            </Button>
                          </a>
                          {(doc.uploaded_by === user?.id || user?.role === "admin") && isActive && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteDocument(doc.id)}
                              disabled={deletingDocId === doc.id}
                              title="Delete"
                            >
                              {deletingDocId === doc.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No documents uploaded yet</p>
                  {isActive && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-4"
                      onClick={() => setUploadDialog(true)}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload First Document
                    </Button>
                  )}
                </div>
              )}}
            </CardContent>
          </Card>
        </div>

        {/* Escrow Payment Section - Show at LOI stage or later */}
        {currentStageIndex >= 3 && (
          <div className="mt-8">
            <EscrowPayment 
              deal={deal} 
              onPaymentSuccess={(updatedDeal) => setDeal(updatedDeal)}
              isBuyer={deal.buyer_id === user?.id}
            />
          </div>
        )}

        {/* Stage History */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Stage History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {deal.stage_history?.map((history, index) => {
                const stageInfo = DEAL_STAGES.find(s => s.key === history.stage);
                const Icon = stageInfo?.icon || Clock;
                
                return (
                  <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className={`p-2 rounded-full ${stageInfo?.color || 'bg-slate-400'} text-white`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-slate-900">{stageInfo?.label || history.stage}</p>
                        <p className="text-sm text-slate-500">
                          {new Date(history.entered_at).toLocaleDateString()}
                        </p>
                      </div>
                      {history.notes && (
                        <p className="text-sm text-slate-600 mt-1">{history.notes}</p>
                      )}
                      {history.completed_at && (
                        <p className="text-xs text-slate-400 mt-1">
                          Completed: {new Date(history.completed_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Advance Stage Dialog */}
      <Dialog open={advanceStageDialog} onOpenChange={setAdvanceStageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Advance to {nextStageInfo?.label}</DialogTitle>
            <DialogDescription>
              {nextStageInfo?.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="stage-notes">Notes (optional)</Label>
              <Textarea
                id="stage-notes"
                placeholder="Add any notes about this stage transition..."
                value={stageNotes}
                onChange={(e) => setStageNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdvanceStageDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdvanceStage} disabled={processing}>
              {processing ? "Processing..." : "Confirm Advance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Deal Dialog */}
      <AlertDialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Cancel Deal
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this deal? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4">
            <Label htmlFor="cancel-reason">Reason for cancellation</Label>
            <Textarea
              id="cancel-reason"
              placeholder="Please provide a reason..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Deal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelDeal}
              className="bg-red-600 hover:bg-red-700"
              disabled={processing}
            >
              {processing ? "Cancelling..." : "Yes, Cancel Deal"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              Upload Document
            </DialogTitle>
            <DialogDescription>
              Upload documents related to this deal. Supported formats: PDF, Word, Excel, Images (max 10MB)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Document Type */}
            <div>
              <Label htmlFor="doc-type">Document Type *</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="mt-2" data-testid="doc-type-select">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* File Input */}
            <div>
              <Label htmlFor="file-upload">File *</Label>
              <div className="mt-2">
                <label 
                  htmlFor="file-upload"
                  className={`
                    flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer
                    ${selectedFile ? 'border-blue-300 bg-blue-50' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'}
                  `}
                >
                  {selectedFile ? (
                    <div className="flex flex-col items-center">
                      <CheckCircle2 className="w-8 h-8 text-blue-600 mb-2" />
                      <p className="text-sm font-medium text-slate-900">{selectedFile.name}</p>
                      <p className="text-xs text-slate-500">{formatFileSize(selectedFile.size)}</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="w-8 h-8 text-slate-400 mb-2" />
                      <p className="text-sm text-slate-600">Click to select a file</p>
                      <p className="text-xs text-slate-400">or drag and drop</p>
                    </div>
                  )}
                  <input
                    id="file-upload"
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    onChange={handleFileSelect}
                    data-testid="file-input"
                  />
                </label>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
              <p><strong>Tip:</strong> Name your files clearly (e.g., "NDA_Signed_April2026.pdf") for easy identification.</p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setUploadDialog(false);
                setSelectedFile(null);
                setDocType("");
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUploadDocument} 
              disabled={uploading || !selectedFile || !docType}
              data-testid="confirm-upload-button"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
